import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  useGetMyGamificationOverviewQuery,
  type AuthorityLevelKey,
} from '../store/api/gamificationApi';
import { LoadErrorNotice } from '../components/LoadErrorNotice';

// Authority level colors
const LEVEL_COLORS: Record<AuthorityLevelKey, { bg: string; text: string; gradient: string }> = {
  novice: { bg: 'bg-gray-100', text: 'text-gray-700', gradient: 'from-gray-400 to-gray-500' },
  bronze: { bg: 'bg-amber-100', text: 'text-amber-700', gradient: 'from-amber-500 to-amber-600' },
  silver: { bg: 'bg-slate-200', text: 'text-slate-700', gradient: 'from-slate-400 to-slate-500' },
  gold: { bg: 'bg-yellow-100', text: 'text-yellow-700', gradient: 'from-yellow-400 to-yellow-500' },
  platinum: { bg: 'bg-purple-100', text: 'text-purple-700', gradient: 'from-purple-500 to-indigo-600' },
};

// Badge rarity colors
const RARITY_COLORS: Record<string, string> = {
  common: 'bg-gray-100 text-gray-600 border-gray-200',
  uncommon: 'bg-green-50 text-green-700 border-green-200',
  rare: 'bg-blue-50 text-blue-700 border-blue-200',
  epic: 'bg-purple-50 text-purple-700 border-purple-200',
  legendary: 'bg-amber-50 text-amber-700 border-amber-200',
};

export function ProfilePage() {
  const { user } = useAuth();
  const {
    data: gamification,
    isLoading: isLoadingGamification,
    isError: gamificationFailed,
    error: gamificationError,
    refetch: refetchGamification,
  } = useGetMyGamificationOverviewQuery();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
        <p className="mt-1 text-gray-500">
          Manage your account and preferences
        </p>
      </div>

      {/* Profile info */}
      <div className="card">
        <div className="card-body">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-3xl font-bold text-primary-700">
                {user?.firstName?.[0] || 'U'}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {user?.firstName} {user?.lastName}
              </h2>
              <p className="text-gray-500">{user?.email}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="badge-primary">{user?.role}</span>
                {gamification && (
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${LEVEL_COLORS[gamification.authority.currentLevel].bg} ${LEVEL_COLORS[gamification.authority.currentLevel].text}`}>
                    {gamification.authority.currentLevelLabel}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Authority & Badges */}
      {gamification && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Authority Level Card */}
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-gray-900">Authority Level</h3>
            </div>
            <div className="card-body">
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-16 h-16 rounded-xl flex items-center justify-center bg-gradient-to-br ${LEVEL_COLORS[gamification.authority.currentLevel].gradient}`}>
                  <TrophyIcon className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h4 className={`text-xl font-bold ${LEVEL_COLORS[gamification.authority.currentLevel].text}`}>
                    {gamification.authority.currentLevelLabel}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {gamification.authority.overallScoreAvg === null ? 'Not measured yet' : `${gamification.authority.overallScoreAvg.toFixed(0)} avg score`} · {gamification.authority.sessionsCount} sessions
                  </p>
                </div>
              </div>

              {gamification.authority.nextLevel && (
                <div className="pt-4 border-t">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Progress to {gamification.authority.nextLevel.label}</span>
                    <span className="font-medium text-gray-900">{gamification.authority.progressToNextLevel.overallProgress.toFixed(0)}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full bg-gradient-to-r ${LEVEL_COLORS[gamification.authority.currentLevel].gradient} transition-all duration-500`}
                      style={{ width: `${Math.min(100, gamification.authority.progressToNextLevel.overallProgress)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-2">
                    <span>Score: {gamification.authority.progressToNextLevel.scoreProgress.toFixed(0)}%</span>
                    <span>Sessions: {gamification.authority.progressToNextLevel.sessionsProgress.toFixed(0)}%</span>
                    <span>Domains: {gamification.authority.progressToNextLevel.domainsProgress.toFixed(0)}%</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Badges Card */}
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Badges Earned</h3>
                <span className="text-sm text-gray-500">{gamification.badges.length} total</span>
              </div>
            </div>
            <div className="card-body">
              {gamification.badges.length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {gamification.badges.slice(0, 6).map((badge) => (
                    <div
                      key={badge.key}
                      className={`p-3 rounded-lg border ${RARITY_COLORS[badge.rarity] || RARITY_COLORS.common}`}
                    >
                      <div className="font-medium text-sm">{badge.label}</div>
                      <div className="text-xs opacity-75 mt-0.5">{badge.description}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <BadgeIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No badges earned yet</p>
                  <p className="text-xs text-gray-400 mt-1">Complete sessions to earn badges!</p>
                </div>
              )}
              {gamification.badges.length > 6 && (
                <p className="text-sm text-gray-500 text-center mt-3">
                  +{gamification.badges.length - 6} more badges
                </p>
              )}
            </div>
          </div>

          {/* Stats Card */}
          <div className="card lg:col-span-2">
            <div className="card-header">
              <h3 className="font-semibold text-gray-900">Practice Stats</h3>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">{gamification.stats.totalSessions}</div>
                  <div className="text-sm text-gray-500">Sessions</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">{gamification.stats.avgScore === null ? '—' : `${gamification.stats.avgScore.toFixed(0)}%`}</div>
                  <div className="text-sm text-gray-500">Avg Score</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">{gamification.stats.currentStreak}</div>
                  <div className="text-sm text-gray-500">Current Streak</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">{gamification.stats.longestStreak}</div>
                  <div className="text-sm text-gray-500">Best Streak</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Level and badges unavailable: said, with Retry — never a blank or a Novice. */}
      {gamificationFailed && !gamification && (
        <div className="card">
          <div className="card-body">
            <LoadErrorNotice
              error={gamificationError}
              onRetry={() => void refetchGamification()}
              failedMessage="Your level and badges are unavailable right now."
              testIdPrefix="profile-gamification"
            />
          </div>
        </div>
      )}

      {isLoadingGamification && (
        <div className="card">
          <div className="card-body flex items-center justify-center py-8">
            <div className="animate-spin w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full" />
          </div>
        </div>
      )}

      {/* Account settings live in Settings (saved through LevelUp Central).
          This page used to carry its own Account / Voice / Notifications /
          Danger Zone cards whose Save, Save Preferences and Delete Account
          buttons did nothing and whose voice list was hard-coded. */}
      <div className="card" data-testid="profile-settings-links">
        <div className="card-header">
          <h3 className="font-semibold text-gray-900">Account &amp; preferences</h3>
        </div>
        <div className="card-body space-y-3 text-sm">
          <p className="text-gray-600">
            Your name, photo and notification preferences are managed in Settings and apply across
            all LevelUp apps.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/settings/profile" className="btn-primary">
              Edit profile
            </Link>
            <Link to="/settings/notifications" className="btn-secondary">
              Notification preferences
            </Link>
          </div>
          {/* Truthful about deletion and export (docs/DATA-LIFECYCLE.md §5: not
              implemented). A workspace administrator can remove a membership,
              which deletes nothing, so they are not offered as a deletion path. */}
          <p className="text-gray-500" data-testid="profile-data-requests">
            Your LevelUp sign-in account is managed by LevelUp Central, not by BeHeard. BeHeard does
            not currently offer in-app deletion or export of your practice data. For an account or
            data request, contact LevelUp support.
          </p>
        </div>
      </div>
    </div>
  );
}

// Icon components
function TrophyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
    </svg>
  );
}

function BadgeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}
